import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import { holdingCompanyService } from '../../api';
import { HoldingCompany } from '../../types/holdingCompany';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 500;
const MIN_SEARCH_LENGTH = 2;
const SCROLL_THRESHOLD_PX = 24;

function buildFilterJson(search: string): string {
  const term = search.trim();

  return JSON.stringify({
    logicalOperator: 'or',
    items: [
      { value: term, columnField: 'nameEn', operatorValue: 'contains' },
      { value: term, columnField: 'nameAr', operatorValue: 'contains' },
      { value: term, columnField: 'ticker', operatorValue: 'contains' },
    ],
  });
}

function mergeUniqueById(prev: HoldingCompany[], next: HoldingCompany[]): HoldingCompany[] {
  const seen = new Set(prev.map((item) => item.id));
  const merged = [...prev];

  for (const item of next) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      merged.push(item);
    }
  }

  return merged;
}

export interface HoldingCompanyAutocompleteProps {
  id: string;
  label: string;
  /** Selected company id (Formik field). 0 means none selected. */
  value: number;
  onChange: (companyId: number) => void;
  /** Seeds the selected label in edit mode before the option is fetched via search. */
  initialCompany?: HoldingCompany | null;
  /** Company ids already picked by sibling rows — excluded from this row's options so the same company can't be attached twice. */
  excludeIds?: number[];
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

export function HoldingCompanyAutocomplete({
  id,
  label,
  value,
  onChange,
  initialCompany,
  excludeIds,
  error,
  helperText,
  disabled,
}: HoldingCompanyAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<HoldingCompany[]>([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(searchText.trim()), SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchText]);

  const fetchPage = useCallback(async (term: string, nextOffset: number, append: boolean) => {
    setLoading(true);
    try {
      const res = await holdingCompanyService.fetchHoldingCompanies({
        limit: PAGE_SIZE,
        offset: nextOffset,
        filter: buildFilterJson(term),
      });
      const items = res.data?.items || [];

      setTotal(res.data?.totalItems ?? 0);
      setOffset(nextOffset + items.length);
      setOptions((prev) => (append ? mergeUniqueById(prev, items) : items));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedSearch.length < MIN_SEARCH_LENGTH) {
      setOptions([]);
      setOffset(0);
      setTotal(0);

      return;
    }

    fetchPage(debouncedSearch, 0, false).catch(() => undefined);
  }, [debouncedSearch, fetchPage]);

  const selectedOption = useMemo(() => {
    if (!value) return null;

    return options.find((option) => option.id === value) || (initialCompany?.id === value ? initialCompany : null);
  }, [value, options, initialCompany]);

  const displayOptions = useMemo(() => {
    const withSelected =
      selectedOption && !options.some((option) => option.id === selectedOption.id)
        ? [selectedOption, ...options]
        : options;

    if (!excludeIds?.length) return withSelected;

    return withSelected.filter((option) => option.id === value || !excludeIds.includes(option.id));
  }, [options, selectedOption, excludeIds, value]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLUListElement>) => {
      const el = event.currentTarget;
      const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_THRESHOLD_PX;

      if (reachedBottom && !loading && offset < total) {
        fetchPage(debouncedSearch, offset, true).catch(() => undefined);
      }
    },
    [loading, offset, total, debouncedSearch, fetchPage],
  );

  return (
    <Autocomplete<HoldingCompany>
      id={id}
      fullWidth
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disabled={disabled}
      value={selectedOption}
      options={displayOptions}
      loading={loading}
      filterOptions={(opts) => opts}
      getOptionLabel={(option) => option.nameEn ?? ''}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_event, newValue) => onChange(newValue ? newValue.id : 0)}
      onInputChange={(_event, newInput, reason) => {
        if (reason === 'input') setSearchText(newInput);
        else if (reason === 'clear') setSearchText('');
      }}
      noOptionsText={debouncedSearch.length < MIN_SEARCH_LENGTH ? 'Type at least 2 characters' : 'No companies found'}
      ListboxProps={{ onScroll: handleScroll }}
      renderInput={(params) => (
        <TextField
          {...params}
          margin="normal"
          label={label}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
